import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Users, AlertCircle, Cpu } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api';
import { PopupField, PopupGridField, PopupCoordinates } from './PopupField';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from '@/components/link/Link';
import { API_SOURCE_PATH } from './constants';
import ViewOnGoogleMaps from './ViewOnGoogleMaps';
import { StopwatchIcon } from '@radix-ui/react-icons';

interface LandownerPopupProps {
	longitude: number;
	latitude: number;
	properties: Record<string, string>;
	onClose: () => void;
}

interface Device {
	id: number;
	site: string;
	siteId: number;
}

interface Ttms {
	id: number;
	city: string;
	state: string;
	ttmsZone: string;
	latitude: number;
	longitude: number;
}

interface LandownerData {
	id: number;
	firstName?: string;
	lastName?: string;
	streetAddress?: string;
	city?: string;
	state?: string;
	zip?: string;
	mailingAddress?: string;
	email?: string;
	phone?: string;
	mobile?: string;
	initialPaymentDate?: string;
	devices?: Device[];
	ttms?: Ttms[];
}

function formatAddress(streetAddress?: string, city?: string, state?: string, zip?: string): string | null {
	const parts = [];
	if (streetAddress) parts.push(streetAddress);

	const cityStateZip = [city, state].filter(Boolean).join(', ');
	if (cityStateZip) {
		parts.push(cityStateZip + (zip ? ` ${zip}` : ''));
	} else if (zip) {
		parts.push(zip);
	}

	return parts.length > 0 ? parts.join(', ') : null;
}

const fetchLandownerData = async (landownerId: string): Promise<LandownerData> => {
	const response = await apiClient.get(`${API_SOURCE_PATH}/landowners/${landownerId}`);

	return response.data;
};

export function LandownerPopup({ longitude, latitude, properties, onClose }: LandownerPopupProps) {
	const {
		data: fetchedData,
		isLoading,
		error
	} = useQuery({
		queryKey: ['geomap', 'landowner', properties.id],
		queryFn: () => fetchLandownerData(properties.id),
		enabled: !!properties.id
	});

	const landownerData: LandownerData = {
		...properties,
		...(fetchedData || {}),
		id: Number(properties.id) || 0
	};

	const displayName =
		`${landownerData.firstName || ''} ${landownerData.lastName || ''}`.trim() || properties.name || 'Unknown';

	const formattedAddress = formatAddress(
		landownerData.streetAddress,
		landownerData.city,
		landownerData.state,
		landownerData.zip
	);

	return (
		<Card className="bg-white dark:bg-slate-900 border shadow-lg min-w-[260px] max-w-[350px]">
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-sm">
						<Users className="h-4 w-4" />
						Landowner Details
					</CardTitle>
					<Button aria-label="close" variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
						<X className="h-3 w-3" />
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<PopupCoordinates longitude={longitude} latitude={latitude} />

				<div className="grid grid-cols-2 gap-2 text-xs">
					<PopupGridField label="ID" value={landownerData.id.toString()} />
					<PopupGridField label="Status" value={properties.status} />
				</div>

				<PopupField label="Name" value={displayName} />

				<ViewOnGoogleMaps latitude={latitude} longitude={longitude} />

				{isLoading && (
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				)}

				{error && (
					<div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
						<AlertCircle className="h-3 w-3" />
						Failed to load additional data
					</div>
				)}

				{fetchedData && (
					<div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
						<PopupField label="Address" value={formattedAddress} />
						<PopupField label="Mailing Address" value={landownerData.mailingAddress} />
						<PopupField label="Email" value={landownerData.email} />
						<PopupField label="Phone" value={landownerData.phone} />
						<PopupField label="Mobile" value={landownerData.mobile} />
						<PopupField
							label="Initial Payment Date"
							value={
								landownerData.initialPaymentDate
									? new Date(landownerData.initialPaymentDate).toLocaleDateString()
									: undefined
							}
						/>

						{landownerData.devices && landownerData.devices.length > 0 && (
							<div>
								<span className="text-slate-600 dark:text-slate-400 text-xs">Devices:</span>
								<div className="flex flex-wrap gap-1 mt-1">
									{landownerData.devices.map(device => (
										<Link key={device.id} href={`/devices/${device.id}`}>
											<Badge
												variant="secondary"
												className="cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1"
											>
												<Cpu className="h-3 w-3" />
												<span>{device.id}</span>
												<span className="text-slate-500 dark:text-slate-400 text-[10px]">
													(Site: {device.siteId})
												</span>
											</Badge>
										</Link>
									))}
								</div>
							</div>
						)}

						{landownerData.ttms && landownerData.ttms.length > 0 && (
							<div>
								<span className="text-slate-600 dark:text-slate-400 text-xs">TTMS:</span>
								<div className="flex flex-wrap gap-1 mt-1">
									{landownerData.ttms.map(ttms => (
										<Link key={ttms.id} href='#'>
											<Badge
												variant="secondary"
												className="cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1"
											>
												<StopwatchIcon className="h-3 w-3" />
												<span>{ttms.id}</span>
												<span className="text-slate-500 dark:text-slate-400 text-[10px]">
													(Zone: {ttms.ttmsZone})
												</span>
											</Badge>
										</Link>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
